import React, { useEffect, useState } from "react";
import Classes from "../Settings.module.css";
import { useDispatch, useSelector } from "react-redux";
import backend from "../../../../../api";
import { uploadMedia } from "../../../../../utils/uploadMedia";
import { setUserData } from "../../../../../redux-store/auth/actions";

export default function InfoTab() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const companyId = user?.companyId;
  const userId = user?._id;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneCountryCode: "+1", // separate code (manual, with +)
    manufacturerName: "",
    manufacturerWebsite: "",
    logo: "", // current logo url (if any)
  });

  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  // load current user + company profile
  useEffect(() => {
    (async () => {
      try {
        const existingPhone = user?.phone || "";
        let phone = existingPhone || "";
        let phoneCountryCode = "+1";

        // split: +XXXYYYYY => +XXX + YYYYY
        if (existingPhone.startsWith("+")) {
          const match = existingPhone.match(/^(\+\d{1,4})(.*)$/);
          if (match) {
            phoneCountryCode = match[1] || "+1";
            phone = (match[2] || "").trim();
          }
        }

        // seed from Redux
        setForm((prev) => ({
          ...prev,
          firstName: user?.firstName || "",
          lastName: user?.lastName || "",
          email: user?.email || "",
          phone,
          phoneCountryCode,
        }));

        if (companyId) {
          const res = await backend.get(`/api/onboarding/${companyId}`);
          const company = res?.data?.data || res?.data; // backend returns { ok, data }, but keep fallback
          if (company) {
            setForm((prev) => ({
              ...prev,
              manufacturerName: company.companyName || "",
              manufacturerWebsite: company.websiteURL || "",
              logo: company.logo || "",
            }));
            setLogoPreview(company.logo || "");
          }
        }
      } catch (err) {
        console.error("Prefill load error:", err?.response?.data || err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId]);

  // cleanup preview URL when file changes
  useEffect(() => {
    if (!logoFile) return;
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 🔁 Shared save logic (used by both Save button and auto-logo upload)
  const saveAll = async (optionalLogoFile) => {
    if (!userId) {
      alert("No user found in session.");
      return;
    }

    setLoading(true);
    try {
      // 1) Upload logo if a new file was chosen
      let logoUrl = form.logo || "";
      const fileToUpload = optionalLogoFile || logoFile;
      if (fileToUpload) {
        const out = await uploadMedia([fileToUpload]);
        logoUrl = Array.isArray(out) ? out[0] : out;
      }

      // 2) Build full phone number with intl code (single value for backend)
      let code = form.phoneCountryCode || "";
      let local = form.phone || "";

      // normalize code: enforce one "+" and only digits after
      if (code) {
        if (!code.startsWith("+")) {
          code = "+" + code.replace(/^\+*/, "");
        }
        code = code.replace(/[^\d+]/g, "");
      }

      // normalize local: digits/spaces/dashes -> for backend we strip spaces/dashes
      local = local.replace(/\s|-/g, "");

      const rawFullPhone = `${code}${local}`;
      const fullPhone = rawFullPhone.replace(/\s|-/g, "");

      // 3) Update user profile
      const userBody = {
        firstName: form.firstName,
        lastName: form.lastName,
        name: [form.firstName, form.lastName].filter(Boolean).join(" "),
        phone: fullPhone,          // single concatenated number with country code
        email: form.email,         // optional: allow changing email
      };
      const userResp = await backend.put(`/api/user/${userId}`, userBody);
      const updatedUser = userResp?.data?.data || userResp?.data;

      // 4) Update company (onboarding)
      if (companyId) {
        const companyBody = {
          companyName: form.manufacturerName,
          websiteURL: form.manufacturerWebsite,
          logo: logoUrl,
        };
        await backend.put(`/api/onboarding/${companyId}`, companyBody);
      }

      // 5) refresh Redux user
      if (updatedUser) {
        await dispatch(setUserData(updatedUser));
      }

      // alert("Settings saved successfully.");

      // 🔄 Refresh screen after save
      window.location.reload();
    } catch (err) {
      console.error("Save error:", err?.response?.data || err.message);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to save settings.";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveAll();
  };

  return (
    <form className={Classes.form} onSubmit={handleSubmit}>
      {/* Upload company logo */}
      <div className={`${Classes.logoSection} ${Classes.mb12}`}>
        <h5 className={`${Classes.label} ${Classes.mb8}`}>Upload your company logo</h5>

        <div className={`${Classes.flexAlignCenter} ${Classes.gap12}`}>
          <div className={Classes.logoPreviewBox}>
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="logo preview"
                className={Classes.logoPreviewImg}
              />
            ) : (
              <span className={Classes.logoCirclePlaceholder} />
            )}
          </div>

          <div>
            <input
              id="companyLogoInput"
              type="file"
              accept="image/*"
              className={Classes.fileInputHidden}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setLogoFile(file);
                  // ⬇️ auto-upload + save + refresh
                  try {
                    await saveAll(file);
                  } catch (err) {
                    console.error("Auto-save after logo upload failed:", err);
                  }
                }
              }}
            />
            <label htmlFor="companyLogoInput" className={Classes.chooseBtn}>
              Choose Image
            </label>
            <div className={`${Classes.miniText2} ${Classes.mt6}`}>
              PNG/JPG, up to ~5MB
            </div>
          </div>
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>First Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="firstName"
            value={form.firstName}
            onChange={onChange}
            placeholder="John"
            required
          />
        </div>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Last Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="lastName"
            value={form.lastName}
            onChange={onChange}
            placeholder="Smith"
            required
          />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Email Address</h5>
          <input
            type="email"
            className={Classes.inputField}
            name="email"
            value={form.email}
            onChange={onChange}
            placeholder="johnsmith@email.com"
            required
          />
        </div>

        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Phone Number</h5>
          <div className={`${Classes.flexAlignCenter} ${Classes.gap12}`}>
            {/* Intl code - manual input, always with + */}
            <input
              type="text"
              className={Classes.inputField}
              name="phoneCountryCode"
              value={form.phoneCountryCode}
              onChange={(e) => {
                let v = e.target.value || "";

                // Ensure a single "+" at the start if something is typed
                if (v.length > 0 && !v.startsWith("+")) {
                  v = "+" + v.replace(/^\+*/, "");
                }

                // Only allow "+" and digits
                v = v.replace(/[^\d+]/g, "");

                setForm((prev) => ({ ...prev, phoneCountryCode: v }));
              }}
              placeholder="+1"
              style={{ maxWidth: "120px" }}
            />

            {/* Local number */}
            <input
              type="text"
              className={Classes.inputField}
              name="phone"
              value={form.phone}
              onChange={(e) => {
                let v = e.target.value || "";
                // Allow digits, spaces and dashes in UI
                v = v.replace(/[^\d\s-]/g, "");
                setForm((prev) => ({ ...prev, phone: v }));
              }}
              placeholder="111 111 1111"
            />
          </div>
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Manufacturer Name</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="manufacturerName"
            value={form.manufacturerName}
            onChange={onChange}
            placeholder="Enter Manufacturer Name"
            required
          />
        </div>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Manufacturer Website</h5>
          <input
            type="text"
            className={Classes.inputField}
            name="manufacturerWebsite"
            value={form.manufacturerWebsite}
            onChange={onChange}
            placeholder="https://www.manufacturerwebsite.com"
          />
        </div>
      </div>

      <div className={Classes.actions}>
        <button type="submit" className={Classes.saveBtn} disabled={loading}>
          <h5>{loading ? "Saving..." : "Save"}</h5>
        </button>
      </div>
    </form>
  );
}
